import { ChangeDetectionStrategy, Component, ViewEncapsulation, inject } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';

import {
    BulkImportSession,
    BulkImportSessionService,
    isTerminalPhase,
} from 'app/core/services/bulk-import-session.service';

/**
 * Cột viên nổi ở góc dưới phải: lối quay lại các cửa sổ nhập lô đang thu nhỏ.
 *
 * Chỉ là VIEW — mọi trạng thái nằm ở `BulkImportSessionService`. Cửa sổ nhập lô cũng dựng ở layout
 * (`bulk-import-host`), nên bấm viên chỉ cần bung ra tại chỗ: KHÔNG điều hướng về trang Nhập lô.
 *
 * KHÔNG có nút "bỏ theo dõi" trên viên. Lượt nhập sống TRONG panel ở trình duyệt: một dấu × ở đây sẽ vừa
 * xoá sạch lô đã đọc vừa không hỏi lại câu nào. Muốn kết thúc thì bung ra rồi đóng cửa sổ — đúng chỗ
 * người dùng nhìn thấy mình đang bỏ đi cái gì.
 *
 * Mọi hàm ở đây trả về SỐ hoặc CHUỖI. Template gọi hàm ở mỗi nhịp change detection, nên hàm trả mảng
 * hoặc đối tượng mới sẽ khiến `@for` dựng lại danh sách liên tục.
 */
@Component({
    selector: 'bulk-import-dock',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [TranslocoModule],
    templateUrl: './bulk-import-dock.component.html',
    styleUrls: ['./bulk-import-dock.component.scss'],
})
export class BulkImportDockComponent {
    readonly session = inject(BulkImportSessionService);
    readonly docked = this.session.dockedSessions;

    /**
     * Dòng đầu nói ĐANG LÀM GÌ, không phải "đang nhập lô hàng" chung chung.
     *
     * Báo giá và tạo lô là hai quãng chờ khác hẳn nhau: quãng đầu chưa có lô nào tồn tại và bấm huỷ
     * không mất gì, quãng sau thì lô đã nằm ở hệ thống hãng. Một nhãn dùng chung cho cả hai giấu mất
     * khác biệt đó.
     */
    phaseLabel(item: BulkImportSession): string {
        return `shipment-import.bulk.dock.phase.${item.Progress.Phase}`;
    }

    /** Lượt đã dừng hẳn: viên thôi quay, đổi màu và nhấp nháy để gọi người dùng vào. */
    isTerminal(item: BulkImportSession): boolean {
        return isTerminalPhase(item.Progress.Phase);
    }

    /**
     * Ba đoạn (xong / chờ đối soát / hỏng) nằm cạnh nhau, phần còn lại là nền — nên chúng KHÔNG bao giờ
     * cộng quá 100% miễn là mẫu số đúng. Chặn dưới 0 để một con số lệch từ panel không đẩy đoạn màu tràn
     * ra ngoài viên.
     */
    percent(item: BulkImportSession, count: number): number {
        const total = item.Progress.Total;
        if (total <= 0) return 0;
        return Math.max(0, Math.min(100, (count / total) * 100));
    }
}
